/**
 * @file Fonctions en ligne de l'application (implemente UiOnlinePort) : pseudo, classements, amis, courrier, evenements, boutique d'evenement,
 * envoi des scores. Les recompenses reclamees aupres du serveur sont creditees dans la partie locale par la commande `server.reward`.
 */
import type { RewardBundle, TicketId } from '@config/types';
import type { Simulation } from '@core/index';
import type { I18n } from '@i18n/index';
import { GameApiError } from '@net/GameClient';
import type { GameClient } from '@net/GameClient';
import type { UiBoard, UiBoardRow, UiEvent, UiFriend, UiMail, UiOffer, UiOnlinePort, UiResult } from '@ui/UiContext';
import type { OnlineService } from './OnlineService';

/** Recompense / prix cote serveur : monnaies et objets (voir GameServer/config/games/catgunner.json). */
interface ServerReward {
  currencies?: Record<string, number>;
  items?: Record<string, number>;
}

const TICKET_IDS: readonly TicketId[] = ['speedRun', 'mine', 'boss', 'raid', 'adventure'];

/** Classements affiches, dans l'ordre : id serveur -> mode d'affichage du score. */
const BOARDS: ReadonlyArray<{ id: string; kind: UiBoard['kind'] }> = [
  { id: 'best_stage', kind: 'stage' },
  { id: 'boss_level', kind: 'level' },
  { id: 'raid_level', kind: 'level' },
  { id: 'catnip_weekly', kind: 'count' },
];

const BADGE_PERIOD_MS = 60_000;
const SCORES_PERIOD_MS = 30_000;

/** Traduit une recompense serveur en lot de recompenses locales (+ texte lisible). Les objets inconnus sont ignores. */
export const mapReward = (r: ServerReward | null | undefined, i18n: I18n): { bundle: RewardBundle; text: string } => {
  const bundle: RewardBundle = {};
  const parts: string[] = [];
  const cur = r?.currencies ?? {};
  const wallet: NonNullable<RewardBundle['wallet']> = {};
  if (cur['dia']) {
    wallet.dia = cur['dia'];
    parts.push(`💎 ${cur['dia']}`);
  }
  if (cur['ruby']) {
    wallet.ruby = cur['ruby'];
    parts.push(`♦ ${cur['ruby']}`);
  }
  if (Object.keys(wallet).length > 0) bundle.wallet = wallet;
  const tickets: NonNullable<RewardBundle['tickets']> = {};
  const draws: NonNullable<RewardBundle['draws']> = [];
  for (const [id, n] of Object.entries(r?.items ?? {})) {
    if (!(n > 0)) continue;
    const t = id.startsWith('ticket_') ? (id.slice(7) as TicketId) : null;
    if (t && TICKET_IDS.includes(t)) {
      tickets[t] = n;
      parts.push(`🎟️ ${i18n.tDyn(`mode.${t}.title`)} ×${n}`);
    } else if (id === 'draw_normal' || id === 'draw_epic') {
      draws.push({ pool: id === 'draw_epic' ? 'epic' : 'normal', count: n });
      parts.push(`${id === 'draw_epic' ? '🌟' : '🎰'} ×${n}`);
    }
  }
  if (Object.keys(tickets).length > 0) bundle.tickets = tickets;
  if (draws.length > 0) bundle.draws = draws;
  return { bundle, text: parts.join('  ') };
};

const fail = (e: unknown): { ok: false; error: string } => ({ ok: false, error: e instanceof GameApiError ? e.code : e instanceof Error && e.message === 'timeout' ? 'timeout' : 'network' });

export class OnlineApi implements UiOnlinePort {
  badge = 0;
  catnipMultiplier = 1;
  /** Appele apres un changement de pseudo reussi (l'app reconnecte alors le salon du hub pour que les autres voient le nouveau pseudo). */
  onRenamed: (() => void) | null = null;
  private readonly listeners = new Set<() => void>();
  private timers: number[] = [];
  private lastScores = '';
  private offBus: Array<() => void> = [];

  constructor(
    private readonly online: OnlineService,
    private readonly sim: Simulation,
    private readonly i18n: I18n,
  ) {}

  get status(): UiOnlinePort['status'] {
    return this.online.status;
  }
  get username(): string | null {
    return this.online.username;
  }

  onStatus(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }

  async retry(): Promise<boolean> {
    const ok = await this.online.retry();
    this.changed();
    if (ok) void this.refreshBadge();
    return ok;
  }

  /** Demarre la surveillance (pastille, envoi periodique des scores). */
  start(): () => void {
    this.offBus.push(this.online.onStatus(() => this.changed()));
    this.offBus.push(this.sim.bus.on('ModeExited', (e) => (e.result?.mode === 'boss' || e.result?.mode === 'raid' ? void this.reportScores() : undefined)));
    this.timers.push(window.setInterval(() => void this.refreshBadge(), BADGE_PERIOD_MS), window.setInterval(() => void this.reportScores(), SCORES_PERIOD_MS));
    void this.refreshBadge();
    void this.reportScores();
    return () => {
      for (const t of this.timers) window.clearInterval(t);
      this.timers = [];
      for (const off of this.offBus) off();
      this.offBus = [];
    };
  }

  private get client(): GameClient | null {
    return this.online.status === 'online' ? this.online.client : null;
  }

  /** Appel protege : hors ligne => erreur `offline`, sinon resultat ou code d'erreur serveur. */
  private async call<T>(fn: (c: GameClient) => Promise<T>): Promise<UiResult<T>> {
    const c = this.client;
    if (!c) return { ok: false, error: 'offline' };
    try {
      return { ok: true, value: await fn(c) };
    } catch (e) {
      return fail(e);
    }
  }

  // ------------------------------------------------------------------------------------------------ scores

  /** Envoie les records locaux (niveau atteint, boss, raid) s'ils ont change. */
  async reportScores(): Promise<void> {
    const d = this.sim.data;
    const scores: Array<[string, number]> = [
      ['best_stage', d.progress.bestStageValue],
      ['boss_level', d.progress.modeBests.boss ?? 0],
      ['raid_level', d.progress.modeBests.raid ?? 0],
    ];
    const key = JSON.stringify(scores);
    if (key === this.lastScores || !this.client) return;
    const c = this.client;
    try {
      for (const [id, v] of scores) if (v > 0) await c.submitScore(id, Math.floor(v));
      this.lastScores = key;
    } catch (e) {
      console.warn('envoi des scores impossible (nouvel essai plus tard)', e);
    }
  }

  // ------------------------------------------------------------------------------------------------ pseudo

  async setNickname(name: string): Promise<UiResult> {
    const clean = name.trim();
    const r = await this.call((c) => c.updateAccount({ username: clean, displayName: clean }));
    if (!r.ok) return r;
    this.online.username = clean;
    this.sim.dispatch({ type: 'profile.nickname', name: clean });
    this.lastScores = ''; // les classements affichent le nouveau pseudo des le prochain envoi
    this.onRenamed?.();
    this.changed();
    return { ok: true, value: undefined };
  }

  // ------------------------------------------------------------------------------------------------ classements

  async leaderboards(): Promise<UiResult<UiBoard[]>> {
    return this.call(async (c) => {
      const defs = ((await c.leaderboards()) as { leaderboards: Array<{ id: string; name: string; resetsAt: number | null }> }).leaderboards;
      const me = this.online.userId;
      const row = (r: { rank: number; username: string; score: number; ownerId: string }): UiBoardRow => ({ rank: r.rank, name: r.username, score: r.score, me: r.ownerId === me });
      return Promise.all(
        BOARDS.map(async (b) => {
          const def = defs.find((d) => d.id === b.id);
          const [top, mine] = await Promise.all([c.records(b.id, { limit: 10 }), c.request<{ record: Parameters<typeof row>[0] | null }>('GET', `/v1/leaderboards/${b.id}/me`)]);
          const rows = ((top as { records: Array<Parameters<typeof row>[0]> }).records ?? []).map(row);
          const self = mine.record ? row(mine.record) : null;
          return { id: b.id, name: def?.name ?? b.id, kind: b.kind, rows, me: self, resetsAt: def?.resetsAt ?? null } satisfies UiBoard;
        }),
      );
    });
  }

  // ------------------------------------------------------------------------------------------------ amis

  async friends(): Promise<UiResult<UiFriend[]>> {
    return this.call(async (c) => {
      const r = (await c.friends()) as { friends: Array<{ profile: { id: string; username: string; displayName: string; online: boolean }; state: string }> };
      return r.friends.flatMap((f) => (f.state === 'friend' || f.state === 'incoming' || f.state === 'outgoing' ? [{ id: f.profile.id, name: f.profile.displayName || f.profile.username, online: f.profile.online, state: f.state } satisfies UiFriend] : []));
    });
  }

  async addFriend(username: string): Promise<UiResult<'sent' | 'friend'>> {
    const r = await this.call((c) => c.addFriend({ username: username.trim() }) as Promise<{ state: string }>);
    if (!r.ok) return r;
    void this.refreshBadge();
    return { ok: true, value: r.value.state === 'friend' ? 'friend' : 'sent' };
  }

  async acceptFriend(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.acceptFriend(id));
    void this.refreshBadge();
    return r.ok ? { ok: true, value: undefined } : r;
  }

  async removeFriend(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.request('DELETE', `/v1/friends/${encodeURIComponent(id)}`));
    void this.refreshBadge();
    return r.ok ? { ok: true, value: undefined } : r;
  }

  // ------------------------------------------------------------------------------------------------ courrier

  async inbox(): Promise<UiResult<UiMail[]>> {
    return this.call(async (c) => {
      const r = (await c.inbox()) as { messages: Array<{ id: string; title: string; body: string; from: string; createdAt: number; read: boolean; claimed: boolean; rewards: ServerReward | null }> };
      return r.messages.map((m) => {
        const text = m.rewards ? mapReward(m.rewards, this.i18n).text : '';
        return { id: m.id, title: m.title, body: m.body, from: m.from, createdAt: m.createdAt, read: m.read, claimed: m.claimed, rewardText: text, claimable: m.rewards !== null && !m.claimed } satisfies UiMail;
      });
    });
  }

  async readMail(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.request('POST', `/v1/inbox/${encodeURIComponent(id)}/read`));
    void this.refreshBadge();
    return r.ok ? { ok: true, value: undefined } : r;
  }

  async claimMail(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.claimMail(id) as Promise<{ rewards: ServerReward }>);
    if (!r.ok) return r;
    this.credit(r.value.rewards, 'mail');
    void this.refreshBadge();
    return { ok: true, value: undefined };
  }

  async deleteMail(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.request('DELETE', `/v1/inbox/${encodeURIComponent(id)}`));
    void this.refreshBadge();
    return r.ok ? { ok: true, value: undefined } : r;
  }

  // ------------------------------------------------------------------------------------------------ evenements + boutique

  async events(): Promise<UiResult<UiEvent[]>> {
    return this.call(async (c) => {
      const r = (await c.liveops()) as { events: { active: RawEvent[]; upcoming: RawEvent[] } };
      return [...r.events.active, ...r.events.upcoming].map((e) => ({
        id: e.id,
        name: e.name,
        description: e.description ?? '',
        active: e.active,
        startsAt: e.startsAt,
        endsAt: e.endsAt,
        goal: e.goal ? { progress: Math.min(e.progress, e.goal.target), target: e.goal.target } : null,
        claimable: e.claimable,
        claimed: e.claimedAt !== null,
        rewardText: e.rewards ? mapReward(e.rewards, this.i18n).text : '',
        multiplier: typeof e.metadata?.['catnipMultiplier'] === 'number' ? (e.metadata['catnipMultiplier'] as number) : null,
      }));
    });
  }

  async claimEvent(id: string): Promise<UiResult> {
    const r = await this.call((c) => c.claimEvent(id) as Promise<{ rewards: ServerReward }>);
    if (!r.ok) return r;
    this.credit(r.value.rewards, 'event');
    void this.refreshBadge();
    return { ok: true, value: undefined };
  }

  /** Offres de la boutique d'evenement (payees en herbe a chat cote serveur) + solde. */
  async shop(): Promise<UiResult<{ offers: UiOffer[]; catnip: number }>> {
    return this.call(async (c) => {
      const [cat, wallet] = await Promise.all([c.catalog() as Promise<{ stores: Record<string, { offers: Array<{ id: string; name: string; price: Record<string, number>; grants: ServerReward; remaining: { perUser: number | null; today: number | null; stock: number | null }; available: boolean }> }> }>, c.wallet()]);
      const offers: UiOffer[] = [];
      for (const o of cat.stores['event']?.offers ?? []) {
        if (!o.available) continue;
        const limits = [o.remaining.perUser, o.remaining.today, o.remaining.stock].filter((n): n is number => n !== null);
        offers.push({ storeId: 'event', id: o.id, name: this.offerName(o.id, o.name), priceCatnip: o.price['catnip'] ?? 0, grantsText: mapReward(o.grants, this.i18n).text, remaining: limits.length ? Math.min(...limits) : null });
      }
      return { offers, catnip: wallet.balances['catnip'] ?? 0 };
    });
  }

  private offerName(id: string, fallback: string): string {
    const k = `online.offer.${id}`;
    const t = this.i18n.tDyn(k);
    return t === k ? fallback : t;
  }

  async buy(offer: UiOffer): Promise<UiResult> {
    const key = `buy:${offer.storeId}:${offer.id}:${Date.now()}`;
    const r = await this.call(async (c) => {
      const grants = ((await c.catalog()) as { stores: Record<string, { offers: Array<{ id: string; grants: ServerReward }> }> }).stores[offer.storeId]?.offers.find((o) => o.id === offer.id)?.grants;
      await c.purchase(offer.storeId, offer.id, key);
      return grants ?? {};
    });
    if (!r.ok) return r;
    this.credit(r.value, `shop:${offer.id}`);
    return { ok: true, value: undefined };
  }

  // ------------------------------------------------------------------------------------------------ interne

  private credit(reward: ServerReward, source: string): void {
    const { bundle } = mapReward(reward, this.i18n);
    this.sim.dispatch({ type: 'server.reward', bundle, source });
    this.sim.bus.emit('Toast', { key: 'online.rewardReceived', kind: 'reward' });
  }

  /** Recompte la pastille (courrier non lu, invitations d'amis, evenements a reclamer) ; silencieux hors ligne. */
  async refreshBadge(): Promise<void> {
    const c = this.client;
    if (!c) return;
    try {
      const [mail, fr, ev] = await Promise.all([c.request<{ unread: number }>('GET', '/v1/inbox?limit=1'), c.friends() as Promise<{ friends: Array<{ state: string }> }>, c.liveops() as Promise<{ events: { active: RawEvent[] } }>]);
      this.catnipMultiplier = ev.events.active.reduce((m, e) => (typeof e.metadata?.['catnipMultiplier'] === 'number' ? Math.max(m, Math.floor(e.metadata['catnipMultiplier'] as number)) : m), 1);
      const n = (mail.unread ?? 0) + fr.friends.filter((f) => f.state === 'incoming').length + ev.events.active.filter((e) => e.claimable).length;
      if (n !== this.badge) {
        this.badge = n;
        this.changed();
      }
    } catch {
      /* hors ligne : la pastille garde sa valeur */
    }
  }
}

interface RawEvent {
  id: string;
  name: string;
  description: string | null;
  startsAt: number;
  endsAt: number;
  active: boolean;
  goal: { metric: string; target: number } | null;
  progress: number;
  claimable: boolean;
  claimedAt: number | null;
  rewards: ServerReward | null;
  metadata?: Record<string, unknown>;
}
